import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, Text, ResponsiveContainer } from 'recharts';
import { getChartData, parseChartData, getChartFill, ChartDataCalculationParams } from '../reportUtilities';
import styles from './DistrictChart.module.scss';
import { DistrictDataByType } from '@peekapak/types/ReportDataExchangeTypes';
import { SchoolModel } from '@peekapak/types/DataProtocolTypes';
import { useEffect, useRef, useState, useLayoutEffect } from 'react';
import { logDebugLevel } from '../../GlobalFunctions';
import GranularityDropdown from '../AdminReportComponents/GranularityDropdown';
import ChartTypeDropdown from '../AdminReportComponents/ChartTypeDropdown';

interface Props {
  data: DistrictDataByType;
  eventToChart: string;
  startDate: Date | undefined;
  endDate: Date | undefined;
  schoolList: SchoolModel[];
  topFiveSchools: string[];
  defaultChartType?: 'bar' | 'line';
}

const MIN_BAR_WIDTH = 20;
const GAP_WIDTH = 5;

const DistrictChart = ({ data, eventToChart, startDate, endDate, schoolList, topFiveSchools, defaultChartType = 'line' }: Props): JSX.Element => {
  const chartContainerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 0);
  const [dataByTimeAndSchool, setDataByTimeAndSchool] = useState(undefined);
  const [barSize, setBarSize] = useState(70);
  const [chartType, setChartType] = useState<'bar' | 'line'>(defaultChartType);
  const [parsedChartData, setParsedChartData] = useState(undefined);
  const [barKeys, setBarKeys] = useState(undefined);
  const [isParametersSet, setIsParametersSet] = useState(false);
  const [forcedNumberOfDaysInCount, setForcedNumberOfDaysInCount] = useState<number>(30);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // ResizeObserver to monitor container width changes
  useEffect(() => {
    const updateWidth = () => {
      if (mountedRef.current && chartContainerRef.current) {
        setContainerWidth(chartContainerRef.current.getBoundingClientRect().width);
      }
    };

    // Update width on mount
    updateWidth();
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (mountedRef.current) {
          setContainerWidth(entry.contentRect.width);
        }
      }
    });

    if (chartContainerRef.current) {
      resizeObserver.observe(chartContainerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Track window width for responsive tick labels
  useLayoutEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate optimal number of bars based on container width and bar width constraints
  useEffect(() => {
    if (!mountedRef.current) return;
    if (containerWidth > 0 && startDate && endDate) {
      const params: ChartDataCalculationParams = {
        data,
        startDate,
        endDate,
        schoolList,
        minBarWidth: MIN_BAR_WIDTH,
        gapWidth: GAP_WIDTH,
        containerWidth,
        forcedNumberOfDaysInCount,
      };

      const dataByTimeAndSchool = getChartData(params);
      setDataByTimeAndSchool(dataByTimeAndSchool);
      const parsedChartData = dataByTimeAndSchool && parseChartData(dataByTimeAndSchool, eventToChart, topFiveSchools);
      setParsedChartData(parsedChartData);
      const barKeys = parsedChartData && Object.keys(parsedChartData[0]).slice(1);
      setBarKeys(barKeys);
      setIsParametersSet(true);
      logDebugLevel(`DistrictChart: Width: ${containerWidth}`);
    } else {
      setIsParametersSet(false);
    }
  }, [containerWidth, eventToChart, forcedNumberOfDaysInCount]);

  return (
    <div ref={chartContainerRef} className={styles.container}>
      {isParametersSet && (
        <>
          <div className='flex items-center gap-4 mb-6'>
            <GranularityDropdown value={forcedNumberOfDaysInCount} onChange={setForcedNumberOfDaysInCount} />
            <ChartTypeDropdown value={chartType} onChange={setChartType} />
          </div>
          <ResponsiveContainer width='100%' height={500}>
            {chartType === 'bar' ? (
              <BarChart width={900} height={300} data={parsedChartData}>
                <XAxis
                  label={{
                    value: '',
                    offset: 0,
                    dy: 15,
                  }}
                  dataKey='name'
                  interval={0}
                  tick={(props) => <CustomXAxisTick {...props} containerWidth={containerWidth} hideDay={forcedNumberOfDaysInCount === 30} />}
                  height={45}
                />
                <YAxis />
                <Tooltip />
                {barKeys &&
                  barKeys.map((key, index) => (
                    <Bar
                      dataKey={key}
                      key={key}
                      stackId='a'
                      fill={getChartFill(key === 'Other' ? 5 : index)}
                      barSize={70}
                    ></Bar>
                  ))}
              </BarChart>
            ) : (
              <LineChart width={900} height={300} data={parsedChartData}>
                <XAxis
                  label={{
                    value: '',
                    offset: 0,
                    dy: 15,
                  }}
                  dataKey='name'
                  interval={0}
                  tick={(props) => <CustomXAxisTick {...props} containerWidth={containerWidth} hideDay={forcedNumberOfDaysInCount === 30} />}
                  height={45}
                />
                <YAxis />
                <Tooltip />
                {barKeys &&
                  barKeys.map((key, index) => (
                    <Line
                      type='monotone'
                      dataKey={key}
                      key={key}
                      stroke={getChartFill(key === 'Other' ? 5 : index)}
                      strokeWidth={2}
                      dot={{ r: 4 }}
                    />
                  ))}
              </LineChart>
            )}
          </ResponsiveContainer>
        </>
      )}
      <h3 className={styles.chartLabel}>Period beginning:</h3>
    </div>
  );
};

interface TickProps {
  x?: number;
  y?: number;
  payload?: { value: string };
  containerWidth?: number;
  hideDay?: boolean;
}
const MEDIUM_BREAKPOINT = 1500;
const CustomXAxisTick = ({ x = 0, y = 0, payload, containerWidth, hideDay }: TickProps) => {
  const xPos = typeof x === 'number' ? x : 0;
  const yPos = typeof y === 'number' ? y : 0;
  if (payload && payload.value) {
    const parts = payload.value.split(' ');
    const month = parts[0] || '';
    const day = parts[1] || '';
    return (
      <>
        <Text fontSize={'14px'} x={xPos} y={yPos + 10} textAnchor='middle'>
          {month}
        </Text>
        {!hideDay && (
          <Text fontSize={'14px'} x={xPos} y={yPos + 26} textAnchor='middle'>
            {day}
          </Text>
        )}
      </>
    );
  }
  return null;
};

export default DistrictChart;
